import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Code, Copy, Check, Eraser } from "lucide-react";
import { looksSensitive, type InsertRow } from "@/lib/statement/core/payload";

// The trust centerpiece (D-008, FR-007). Renders the EXACT payload array that
// will be inserted — the same object the parent sends — as a table and as raw
// JSON, with a per-row scrub for residual PII. Used by both the PDF and CSV
// imports. The parent passes the same `rows` reference it will POST.

interface Props {
  rows: InsertRow[];
  onConfirm: () => void;
  onBack: () => void;
  onScrub?: (index: number) => void;
  busy?: boolean;
  formatAmount?: (n: number) => string;
}

export const WhatLeavesReceipt = ({ rows, onConfirm, onBack, onScrub, busy, formatAmount }: Props) => {
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const fmt = formatAmount ?? ((n: number) => n.toFixed(2));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rows, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-4">
        <ShieldCheck className="h-5 w-5 shrink-0 text-income-text" />
        <div>
          <p className="text-sm font-semibold">This is everything that leaves your device</p>
          <p className="text-xs text-muted-foreground">
            {rows.length} row{rows.length === 1 ? "" : "s"} · 0 files. Your PDF, name, IBAN and address stay on your device and are discarded when you close this.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setShowJson((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
          <Code className="h-3.5 w-3.5" /> {showJson ? "Show table" : "Show raw JSON"}
        </button>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
          {copied ? <Check className="h-3.5 w-3.5 text-income-text" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {showJson ? (
        <pre className="max-h-72 overflow-auto rounded-lg border bg-muted/40 p-3 text-xs" data-testid="receipt-json">
          {JSON.stringify(rows, null, 2)}
        </pre>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded-lg border" data-testid="receipt-table">
          {rows.map((r, i) => {
            const sensitive = looksSensitive(r.description);
            return (
              <div key={i} className="flex items-center gap-3 border-b px-3 py-2 text-xs last:border-b-0">
                <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{r.date}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{r.description}</span>
                    {sensitive && (
                      <button type="button" onClick={() => onScrub?.(i)} className="inline-flex shrink-0 items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                        <Eraser className="h-3 w-3" /> scrub
                      </button>
                    )}
                  </div>
                </div>
                <Badge variant="secondary" className="shrink-0">{r.category}</Badge>
                <span className={`w-20 shrink-0 text-right font-medium tabular-nums ${r.type === "income" ? "text-income-text" : "text-destructive"}`}>
                  {r.type === "income" ? "+" : "-"}{fmt(r.amount)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onBack} disabled={busy}>Back</Button>
        <Button className="flex-1" onClick={onConfirm} disabled={busy || rows.length === 0}>
          {busy ? "Sending…" : `Send ${rows.length} row${rows.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </div>
  );
};
